// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * API functions for the SFDC drift-resolution endpoints. Three calls:
 * - GET diff: list per-field discrepancies
 * - POST pull: write the SFDC value into Hardfin
 * - POST dismiss: accept the divergence
 *
 * All three are path-generic over entityType — see SFDCDriftEntityType.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  SFDCDriftDiffResponse,
  SFDCDriftEntityType,
  SFDCDriftResolutionResponse,
  sfdcDriftDiffResponseSchema,
  sfdcDriftResolutionResponseSchema,
} from '@/sfdc/api/types';

function driftBasePath(
  entityType: SFDCDriftEntityType,
  entityKey: string
): string {
  return `/v1/crm/sfdc/drift/${entityType}/${entityKey}`;
}

export async function getSFDCDriftDiff(
  entityType: SFDCDriftEntityType,
  entityKey: string
): Promise<SFDCDriftDiffResponse> {
  const path = `${driftBasePath(entityType, entityKey)}/diff`;
  const response = await api.get(path);
  return parseResponse(sfdcDriftDiffResponseSchema, response.data, {
    schemaName: 'SFDCDriftDiff',
    endpoint: path,
  });
}

export async function pullSFDCDriftField(
  entityType: SFDCDriftEntityType,
  entityKey: string,
  fieldName: string
): Promise<SFDCDriftResolutionResponse> {
  const path = `${driftBasePath(entityType, entityKey)}/pull`;
  const response = await api.post(path, { fieldName });
  return parseResponse(sfdcDriftResolutionResponseSchema, response.data, {
    schemaName: 'PullSFDCDriftField',
    endpoint: path,
  });
}

export async function dismissSFDCDriftField(
  entityType: SFDCDriftEntityType,
  entityKey: string,
  fieldName: string
): Promise<SFDCDriftResolutionResponse> {
  const path = `${driftBasePath(entityType, entityKey)}/dismiss`;
  const response = await api.post(path, { fieldName });
  return parseResponse(sfdcDriftResolutionResponseSchema, response.data, {
    schemaName: 'DismissSFDCDriftField',
    endpoint: path,
  });
}
